/**
 * AdminMasterDiscGroups — disc-grouped master file list for Admin Order Detail.
 *
 *  • Renders master files grouped per disc when the order has >1 master slot.
 *  • For each disc, shows an editable sub-catalogue number (defaults to
 *    `{customer_catalogue_number}-{disc_index}`, or just `Disc N` when the
 *    catalogue number is missing). Manual edits persist on the slot and
 *    survive future changes to the main catalogue number.
 *  • For VINYL orders: slot labels follow the customer-upload convention —
 *    "Side A", "Side B", "Side C", "Side D", … with a "Vinyl N – " prefix
 *    when the release spans more than one record (slot pair = one vinyl).
 *    This mirrors the customer upload UI so repro can immediately tell
 *    which master belongs to which side of which record.
 *  • Admin-only. Customers / repro see the existing flat layout via the
 *    `MasterFilesCard` component.
 */
import { useState } from 'react';
import { Music, Download, Pencil, Check as CheckIcon, X as CloseIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { ordersApi } from '../lib/api';
import { RejectMasterFileDialog } from './RejectMasterFileDialog';
import { RemoveMasterFileDialog } from './RemoveMasterFileDialog';
import { AdminMasterFileRow } from './AdminMasterFileRow';

/**
 * Build the per-slot default sub-catalogue number from the order's main
 * catalogue number. Mirrors the spec: `{cat}-{n}`, fallback `{slot label}`.
 */
const buildDefaultSubCat = (mainCat, slotIndex, slotLabel) => {
    const trimmed = (mainCat || '').trim();
    if (!trimmed) return slotLabel;
    return `${trimmed}-${slotIndex}`;
};

/**
 * Vinyl side letter for a given 0-based slot index. Slot 0 → A, 1 → B,
 * 2 → C … 25 → Z. Beyond 26 sides we wrap with a numeric suffix so the
 * label is always meaningful even for absurdly large releases.
 */
const sideLetterForIndex = (idx) => {
    if (idx < 26) return String.fromCharCode(65 + idx);
    const cycle = Math.floor(idx / 26);
    const letter = String.fromCharCode(65 + (idx % 26));
    return `${letter}${cycle + 1}`;
};

/**
 * Resolve the label shown in the slot header. For vinyl: always recompute
 * from slot index so the naming convention matches the customer-facing
 * upload page exactly ("Vinyl 1 – Side A" etc.). For other product types
 * we keep the legacy "Disc N" wording.
 */
const buildSlotLabel = (productType, slotIndex, totalSlots) => {
    if ((productType || '').toLowerCase() === 'vinyl') {
        const sideLetter = sideLetterForIndex(slotIndex);
        // 2 sides per vinyl. Floor-divide the slot index to get the vinyl
        // number. Only show the "Vinyl N –" prefix when the release has
        // more than one record (totalSlots > 2) — for a single-record
        // 2-side release the prefix is redundant.
        if (totalSlots > 2) {
            const vinylNumber = Math.floor(slotIndex / 2) + 1;
            return `Vinyl ${vinylNumber} – Side ${sideLetter}`;
        }
        return `Side ${sideLetter}`;
    }
    return `Disc ${slotIndex + 1}`;
};

/**
 * Group files into buckets keyed by master slot id — and, when a slot has
 * DVD-9-style dual-layer files, sub-group by layer so Layer 0 and Layer 1
 * (which share filenames like `MAIN.DAT`, `CONTROL.DAT`, `DDPID`) render
 * in visually distinct sections.
 *
 * Shape:
 *   {
 *     <slotId>: { layers: { layer0: [...], layer1: [...], main: [...] } },
 *     __unassigned__: [...],
 *   }
 *
 * The `main` bucket holds any file whose category is `master_slot_<id>`
 * with NO `_layerN` suffix — these are un-layered files that belong to
 * the disc as a whole (e.g. legacy uploads or single-layer discs).
 */
const groupFilesBySlot = (files) => {
    const buckets = {};
    for (const f of files) {
        const cat = f.category || '';
        if (!cat.startsWith('master_slot_')) {
            (buckets.__unassigned__ ||= []).push(f);
            continue;
        }
        const tail = cat.slice('master_slot_'.length);
        const layerMatch = tail.match(/^(.+)_layer(\d)$/);
        const slotId = layerMatch ? layerMatch[1] : tail;
        const layerKey = layerMatch ? `layer${layerMatch[2]}` : 'main';

        const slotBucket = (buckets[slotId] ||= { layers: { main: [], layer0: [], layer1: [] } });
        (slotBucket.layers[layerKey] ||= []).push(f);
    }
    return buckets;
};

/**
 * Return true if a slot bucket has any layer-scoped files. Used by the
 * renderer to decide between a flat list vs. Layer 0 / Layer 1 sub-cards.
 */
const slotHasLayeredFiles = (bucket) => {
    if (!bucket || !bucket.layers) return false;
    return (bucket.layers.layer0 || []).length > 0
        || (bucket.layers.layer1 || []).length > 0;
};

function DiscHeader({ slot, slotIndex, slotLabel, mainCatNumber, orderId, onSlotUpdated }) {
    const persisted = (slot.sub_catalogue_number || '').trim();
    const defaultSubCat = buildDefaultSubCat(mainCatNumber, slotIndex + 1, slotLabel);
    const displaySubCat = persisted || defaultSubCat;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(persisted);
    const [saving, setSaving] = useState(false);

    const startEdit = () => {
        // Pre-fill with the current shown value so admin starts from the
        // visible default instead of an empty field.
        setDraft(persisted || defaultSubCat);
        setEditing(true);
    };

    const cancel = () => {
        setEditing(false);
        setDraft(persisted);
    };

    const save = async () => {
        const cleaned = draft.trim();
        // Empty draft = clear the manual override so we fall back to the
        // auto-generated default again. Anything else (including the same
        // value as default) is stored verbatim and treated as authoritative.
        const willClear = cleaned.length === 0;
        const newValue = willClear ? null : cleaned;
        if ((newValue || '') === (persisted || '')) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await ordersApi.updateMasterSlot(orderId, slot.id, {
                sub_catalogue_number: willClear ? '' : cleaned,
            });
            onSlotUpdated?.({ ...slot, sub_catalogue_number: newValue });
            toast.success(
                willClear ? 'Reset to default catalogue number' : 'Catalogue number saved',
            );
            setEditing(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save catalogue number');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="flex items-center justify-between mb-2"
            data-testid={`disc-header-${slot.id}`}
        >
            <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-semibold text-slate-700">
                    {slotLabel}
                </span>
                {editing ? (
                    <div className="flex items-center gap-1">
                        <Input
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder={defaultSubCat}
                            disabled={saving}
                            className="h-6 text-[11px] px-2 w-[180px]"
                            data-testid={`disc-sub-cat-input-${slot.id}`}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') save();
                                if (e.key === 'Escape') cancel();
                            }}
                        />
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-emerald-600 hover:text-emerald-700"
                            onClick={save}
                            disabled={saving}
                            data-testid={`disc-sub-cat-save-${slot.id}`}
                            title="Save"
                        >
                            <CheckIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                            onClick={cancel}
                            disabled={saving}
                            data-testid={`disc-sub-cat-cancel-${slot.id}`}
                            title="Cancel"
                        >
                            <CloseIcon className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={startEdit}
                        className="group inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-800 transition-colors"
                        title="Edit catalogue number for this slot"
                        data-testid={`disc-sub-cat-edit-${slot.id}`}
                    >
                        <span className="font-mono">({displaySubCat})</span>
                        <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        {persisted && (
                            <span
                                className="text-[9px] uppercase tracking-wider px-1 py-px rounded bg-indigo-50 text-indigo-600 border border-indigo-100"
                                title="Manually set — won't auto-update if the main catalogue number changes"
                            >
                                manual
                            </span>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}

export function AdminMasterDiscGroups({
    order,
    audioFiles,
    onDownloadFile,
    onOrderUpdated,
    onFileUpdated,
}) {
    const slots = order?.master_slots || [];
    const [rejectingFile, setRejectingFile] = useState(null);
    const [removingFile, setRemovingFile] = useState(null);

    // Empty state — nothing to render, but don't hide the layer UX for a
    // single-slot DVD-9 (dual-layer) order: the disc groups reveal the
    // Layer 0 / Layer 1 split even when there's only one physical disc.
    if (slots.length === 0) return null;

    const buckets = groupFilesBySlot(audioFiles);
    const mainCat = order?.catalogue_number || '';
    const productType = order?.product_type;
    const isVinyl = (productType || '').toLowerCase() === 'vinyl';

    const handleSlotUpdated = (updatedSlot) => {
        // Optimistically update the parent order so the new sub-cat value
        // sticks after toggling section open/closed without a refetch.
        const nextSlots = slots.map((s) =>
            s.id === updatedSlot.id ? { ...s, ...updatedSlot } : s,
        );
        onOrderUpdated?.({ ...order, master_slots: nextSlots });
    };

    return (
        <div className="space-y-3" data-testid="admin-master-disc-groups">
            {slots.map((slot, idx) => {
                const slotLabel = buildSlotLabel(productType, idx, slots.length);
                const slotBucket = buckets[slot.id];
                const hasLayers = slotHasLayeredFiles(slotBucket);
                const flatFiles = slotBucket
                    ? [
                        ...(slotBucket.layers.main || []),
                        // When there are NO layered files, fall back to
                        // flat rendering of everything under this slot.
                        ...(hasLayers ? [] : [
                            ...(slotBucket.layers.layer0 || []),
                            ...(slotBucket.layers.layer1 || []),
                        ]),
                    ]
                    : [];
                return (
                    <div
                        key={slot.id}
                        className={`border rounded-lg p-2.5 ${
                            isVinyl
                                ? 'border-amber-200 bg-amber-50/30'
                                : 'border-slate-200 bg-slate-50/40'
                        }`}
                        data-testid={`disc-group-${slot.id}`}
                        data-slot-label={slotLabel}
                    >
                        <DiscHeader
                            slot={slot}
                            slotIndex={idx}
                            slotLabel={slotLabel}
                            mainCatNumber={mainCat}
                            orderId={order.id}
                            onSlotUpdated={handleSlotUpdated}
                        />

                        {/* Layer sub-cards — only rendered when the disc has
                            dual-layer files. Each layer is a distinct visual
                            block so it's obvious which manifest and DAT
                            files belong together (DVD-9 authoring produces
                            identical filenames on both layers). */}
                        {hasLayers && (
                            <div className="mt-1 space-y-2">
                                {['layer0', 'layer1'].map((layerKey) => {
                                    const layerFiles = slotBucket.layers[layerKey] || [];
                                    if (layerFiles.length === 0) return null;
                                    const layerNum = layerKey === 'layer0' ? 0 : 1;
                                    return (
                                        <div
                                            key={layerKey}
                                            className="border border-slate-200 bg-white rounded-md"
                                            data-testid={`disc-layer-group-${slot.id}-${layerKey}`}
                                        >
                                            <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-100 bg-slate-50/60">
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                                                            layerNum === 0
                                                                ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                                                                : 'bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200'
                                                        }`}
                                                        title={`DVD-9 dual-layer — Layer ${layerNum}`}
                                                    >
                                                        L{layerNum}
                                                    </span>
                                                    <span className="text-[11px] font-semibold text-slate-700">
                                                        Layer {layerNum}
                                                    </span>
                                                    <span className="text-[10px] text-slate-400">
                                                        {layerFiles.length} file{layerFiles.length === 1 ? '' : 's'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="px-2.5 py-2 space-y-1">
                                                {layerFiles.map((file) => (
                                                    <AdminMasterFileRow
                                                        key={file.id}
                                                        file={file}
                                                        onDownload={onDownloadFile}
                                                        onReject={setRejectingFile}
                                                        onRemove={setRemovingFile}
                                                        onFileUpdated={onFileUpdated}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Un-layered files uploaded to the same slot
                                    (rare — usually cover art or shared assets).
                                    Kept visible under the layer cards so nothing
                                    silently disappears. */}
                                {(slotBucket.layers.main || []).length > 0 && (
                                    <div
                                        className="border border-slate-200 bg-white rounded-md"
                                        data-testid={`disc-layer-group-${slot.id}-shared`}
                                    >
                                        <div className="px-2.5 py-1.5 border-b border-slate-100 bg-slate-50/60">
                                            <span className="text-[11px] font-semibold text-slate-600">
                                                Shared / un-layered
                                            </span>
                                        </div>
                                        <div className="px-2.5 py-2 space-y-1">
                                            {slotBucket.layers.main.map((file) => (
                                                <AdminMasterFileRow
                                                    key={file.id}
                                                    file={file}
                                                    onDownload={onDownloadFile}
                                                    onReject={setRejectingFile}
                                                    onRemove={setRemovingFile}
                                                    onFileUpdated={onFileUpdated}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Flat rendering — single-layer disc or legacy uploads. */}
                        {!hasLayers && flatFiles.length > 0 && (
                            <div className="space-y-1">
                                {flatFiles.map((file) => (
                                    <AdminMasterFileRow
                                        key={file.id}
                                        file={file}
                                        onDownload={onDownloadFile}
                                        onReject={setRejectingFile}
                                        onRemove={setRemovingFile}
                                        onFileUpdated={onFileUpdated}
                                    />
                                ))}
                            </div>
                        )}

                        {!hasLayers && flatFiles.length === 0 && (
                            <p className="text-[11px] text-slate-400 italic">
                                {isVinyl
                                    ? 'No master uploaded for this side'
                                    : 'No files uploaded for this disc'}
                            </p>
                        )}
                    </div>
                );
            })}

            {(buckets.__unassigned__ || []).length > 0 && (
                <div className="border border-amber-200 rounded-lg p-2.5 bg-amber-50/40">
                    <div className="text-xs font-semibold text-amber-700 mb-2">
                        Unassigned files
                    </div>
                    <div className="space-y-1">
                        {buckets.__unassigned__.map((file) => (
                            <AdminMasterFileRow
                                key={file.id}
                                file={file}
                                onDownload={onDownloadFile}
                                onReject={setRejectingFile}
                                onRemove={setRemovingFile}
                                onFileUpdated={onFileUpdated}
                            />
                        ))}
                    </div>
                </div>
            )}

            <RejectMasterFileDialog
                open={!!rejectingFile}
                onOpenChange={(open) => { if (!open) setRejectingFile(null); }}
                orderId={order?.id}
                file={rejectingFile}
                onRejected={(updatedFile) => {
                    setRejectingFile(null);
                    onFileUpdated?.(updatedFile);
                }}
            />
            <RemoveMasterFileDialog
                open={!!removingFile}
                onOpenChange={(open) => { if (!open) setRemovingFile(null); }}
                orderId={order?.id}
                file={removingFile}
                onRemoved={(updatedFile) => {
                    setRemovingFile(null);
                    if (updatedFile) onFileUpdated?.(updatedFile);
                }}
            />
        </div>
    );
}

export default AdminMasterDiscGroups;
